import {
  Children,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode
} from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle02Icon } from "@hugeicons-pro/core-stroke-rounded";
import type { BoomHelpCrumb, BoomHelpScreen } from "api/boom-help/types";
import { SCREEN_SCHEME, toMarkdownWithScreenLinks } from "../screen-links";
import { ANSWER_TEXT_CLASS } from "../constants";
import { usePortalPathAvailable } from "../use-portal-path-available";

const LINK_CLASS =
  "font-medium text-primary-500 underline-offset-2 hover:underline";
const PLAIN_CLASS = "font-medium text-content-1000";

const urlTransform = (url: string) =>
  url.startsWith(SCREEN_SCHEME) ? url : defaultUrlTransform(url);

const crumbsOf = (screen: BoomHelpScreen): BoomHelpCrumb[] =>
  screen.crumbs?.length
    ? screen.crumbs
    : [{ label: screen.label, path: screen.path, linkable: screen.linkable }];

const ScreenPath = ({ screen }: { screen: BoomHelpScreen }) => {
  const isPathAvailable = usePortalPathAvailable();
  return (
    <span>
      {crumbsOf(screen).map((crumb, index) => (
        <Fragment key={`${crumb.label}-${index}`}>
          {index > 0 ? <span className="text-content-300"> → </span> : null}
          {crumb.linkable && crumb.path && isPathAvailable(crumb.path) ? (
            <Link to={crumb.path} className={LINK_CLASS}>
              {crumb.label}
            </Link>
          ) : (
            <span className={PLAIN_CLASS}>{crumb.label}</span>
          )}
        </Fragment>
      ))}
    </span>
  );
};

const Checklist = ({ children }: { children: ReactNode }) => (
  <ul className="space-y-2">
    {Children.toArray(children)
      .filter((child): child is ReactElement<{ children?: ReactNode }> =>
        isValidElement(child)
      )
      .map((item, index) => (
        <li key={item.key ?? index} className="flex gap-2">
          <HugeiconsIcon
            icon={CheckmarkCircle02Icon}
            size={18}
            className="mt-0.5 shrink-0 text-content-300"
          />
          <span className="min-w-0">{item.props.children}</span>
        </li>
      ))}
  </ul>
);

export const AnswerContent = ({
  content,
  screens
}: {
  content: string;
  screens: BoomHelpScreen[];
}) => {
  const byId = new Map(screens.map((screen) => [screen.id, screen]));

  return (
    <div className={ANSWER_TEXT_CLASS}>
      <ReactMarkdown
        urlTransform={urlTransform}
        components={{
          a: ({ href, children }) => {
            const screen = href?.startsWith(SCREEN_SCHEME)
              ? byId.get(href.slice(SCREEN_SCHEME.length))
              : undefined;
            if (screen) return <ScreenPath screen={screen} />;
            return <span className={PLAIN_CLASS}>{children}</span>;
          },
          ul: ({ children }) => <Checklist>{children}</Checklist>
        }}
      >
        {toMarkdownWithScreenLinks(content, screens)}
      </ReactMarkdown>
    </div>
  );
};
